import React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { BluecoinsData, BluecoinStatus } from '../Bluecoins/types';
import {
  bluecoinsAtom,
  isBluecoinListPanelOpenAtom,
  isBluecoinPanelOpenAtom,
} from '../Bluecoins/BluecoinState';
import { autofocusAtom } from '../OnlineMapState';
import useAnalytics from '../../../hooks/useAnalytics';
import {
  mapInfoWindowBluecoinNameAtom,
  mapInfoWindowIdentificationAtom,
} from '../ToolbarLayer/VehicleChipsControl/InfoWindowState';
import MarkerAnimatedIcon from '../Bluecoins/MarkerAnimatedIcon';
import MarkerStaticIcon from '../Bluecoins/MarkerStaticIcon';

type BluecoinMarkerProps = {
  map: google.maps.Map | undefined;
  lat: number;
  lng: number;
  zIndex: number;
  bluecoinIds: number[];
};

type BluecoinIconOptions = {
  animatedIcon?: boolean;
  isSelected?: boolean;
};

function BluecoinMarker(props: BluecoinMarkerProps): React.JSX.Element {
  const bluecoinsData = useRecoilValue<BluecoinsData>(bluecoinsAtom);
  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const isIdentificationEnabled = useRecoilValue<boolean>(mapInfoWindowIdentificationAtom);
  const isBluecoinNameEnabled = useRecoilValue<boolean>(mapInfoWindowBluecoinNameAtom);
  const [isBluecoinPanelOpen, setIsBluecoinPanelOpen] = useRecoilState<number | false>(
    isBluecoinPanelOpenAtom
  );
  const setIsBluecoinListPanelOpen = useSetRecoilState<number[] | false>(
    isBluecoinListPanelOpenAtom
  );
  const { trackEvent } = useAnalytics();
  const isSelected = (): boolean => {
    return props.bluecoinIds.includes(Number(isBluecoinPanelOpen));
  };

  const getIcon = (status: BluecoinStatus, options?: BluecoinIconOptions): React.JSX.Element => {
    let baseColor = '#146755';
    let strokeColor = '#05B590';
    if (status === 'INACTIVE') {
      baseColor = '#737373';
      strokeColor = '#B6B6B6';
    }
    if (status === 'ACTIVE' && options?.isSelected) {
      baseColor = '#FFFFFF';
      strokeColor = '#05B590';
    }
    if (status === 'INACTIVE' && options?.isSelected) {
      baseColor = '#FFFFFF';
      strokeColor = '#B6B6B6';
    }
    if (options?.animatedIcon) {
      return <MarkerAnimatedIcon markerColor={baseColor} strokeColor={strokeColor} />;
    }
    return <MarkerStaticIcon markerColor={baseColor} strokeColor={strokeColor} />;
  };

  const isActive: boolean =
    props.bluecoinIds.filter(
      (bluecoinId: number) => bluecoinsData[bluecoinId] && bluecoinsData[bluecoinId].attached
    ).length > 0;

  const getBluecoinNames = (bluecoinIds: number[]): string[] => {
    const bluecoinNames: string[] = [];
    bluecoinIds.forEach((bluecoinId: number) => {
      if (bluecoinsData[bluecoinId]) {
        bluecoinNames.push(bluecoinsData[bluecoinId].name);
      }
    });
    return bluecoinNames;
  };

  const handleOnClickMarker = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['map', 'click', `Select bluecoin via marker ${props.bluecoinIds.join()}`]);
    const bluecoinId = event.currentTarget.getAttribute('data-id');

    if (bluecoinId && props.bluecoinIds.length === 1) {
      setIsBluecoinPanelOpen((oldState: number | false) => {
        if (oldState === Number(bluecoinId)) {
          return false;
        }
        return Number(bluecoinId);
      });

      const position = new google.maps.LatLng(props.lat, props.lng);
      const mapBounds = new google.maps.LatLngBounds();
      mapBounds.extend(position);

      if (props.map) {
        setAutofocus(false);
        props.map.fitBounds(mapBounds, { top: 50, right: 50, bottom: 100, left: 300 });
        props.map.setCenter(position);
        props.map.setZoom(20);
      }
    }
    if (props.bluecoinIds.length > 1) {
      setIsBluecoinPanelOpen(false);
      setIsBluecoinListPanelOpen(props.bluecoinIds);
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        cursor: 'pointer',
        zIndex: props.zIndex,
      }}
      onClick={handleOnClickMarker}
      id={`bluecoin-marker-${props.bluecoinIds.join('')}`}
      data-id={props.bluecoinIds.join('')}
      data-cy={`bluecoin-marker-${props.bluecoinIds.join('')}`}
    >
      {isIdentificationEnabled && isBluecoinNameEnabled && props.bluecoinIds.length === 1 && (
        <div
          className="e-map-pin-info fixed-width"
          style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translate(-50%)' }}
        >
          {getBluecoinNames(props.bluecoinIds).map((name: string, index: number) => (
            <span className="info" key={`bluecoin-name-${index}`}>
              {name}
            </span>
          ))}
        </div>
      )}
      {props.bluecoinIds.length > 1 && (
        <div
          className="e-map-pin-info fixed-width"
          style={{
            position: 'absolute',
            bottom: '0',
            transform: 'translate(10%, -225%)',
          }}
        >
          <div>
            <span className="info" data-cy={`bluecoin-marker-count-${props.bluecoinIds.join('')}`}>
              {props.bluecoinIds.length}
            </span>
          </div>
        </div>
      )}
      {getIcon(isActive ? 'ACTIVE' : 'INACTIVE', {
        isSelected: isSelected(),
        animatedIcon: true,
      })}
    </div>
  );
}

export default React.memo(BluecoinMarker);
